import type { PaletteKey } from '../palette';
import { PixelCanvas } from './canvas';
import type { Pixel } from './grid';

/**
 * Shading and finishing helpers shared by procedural sprites: ramp stepping, selective outlines ("sel-out"),
 * lit volumes, blobs, value noise and ordered dither. Adopted with the Storybook art direction (see
 * .claude/skills/pixel-art/reference/style-guide.md).
 */
/** One step darker along the colour's own material ramp (hue-shifted towards purple/navy in the darks). */
export const DARKER: Record<PaletteKey, PaletteKey> = {
  k: 'k',
  '1': 'k',
  '2': '1',
  '3': '2',
  '4': '3',
  '5': '4',
  w: '5',
  N: 'k',
  n: 'N',
  m: 'n',
  t: 'm',
  e: 't',
  S: 'n',
  s: 'S',
  a: 'n',
  R: 'N',
  r: 'R',
  x: 'R',
  i: 'r',
  u: 'N',
  U: 'u',
  o: 'U',
  Y: 'U',
  y: 'Y',
  D: 'k',
  G: 'D',
  g: 'G',
  l: 'g',
  B: '1',
  b: 'B',
  c: 'b',
  P: 'k',
  p: 'P',
};

/** One step lighter along the colour's material ramp (hue-shifted towards yellow in the lights). */
export const LIGHTER: Record<PaletteKey, PaletteKey> = {
  k: '1',
  '1': '2',
  '2': '3',
  '3': '4',
  '4': '5',
  '5': 'w',
  w: 'w',
  N: 'n',
  n: 'm',
  m: 't',
  t: 'e',
  e: 'w',
  S: 's',
  s: 'e',
  a: 'm',
  R: 'r',
  r: 'i',
  x: 'i',
  i: 'e',
  u: 'U',
  U: 'o',
  o: 'Y',
  Y: 'y',
  y: 'w',
  D: 'G',
  G: 'g',
  g: 'l',
  l: 'y',
  B: 'b',
  b: 'c',
  c: 'w',
  P: 'p',
  p: 'i',
};

export const darker = (c: PaletteKey, n = 1): PaletteKey => {
  let out = c;
  for (let i = 0; i < n; i++) out = DARKER[out];
  return out;
};
export const lighter = (c: PaletteKey, n = 1): PaletteKey => {
  let out = c;
  for (let i = 0; i < n; i++) out = LIGHTER[out];
  return out;
};

const N4 = [
  [0, -1],
  [1, 0],
  [0, 1],
  [-1, 0],
] as const;

/**
 * Selective outline ("sel-out"): every transparent pixel touching the shape becomes a darker version of the
 * colour it touches instead of flat black. `steps` = how much darker (2 is the classic look). Edges on the
 * lit top/left side are one step lighter than `steps`; with `ground` set, the bottom edge (pixels below the
 * shape) uses that colour (usually `k`) so the sprite still sits firmly on the ground.
 */
export function selout(
  c: PixelCanvas,
  opts: { steps?: number; litSteps?: number; ground?: PaletteKey | null; diagonal?: boolean } = {},
): PixelCanvas {
  const steps = opts.steps ?? 2;
  const lit = opts.litSteps ?? Math.max(1, steps - 1);
  const add: [number, number, PaletteKey][] = [];
  for (let y = 0; y < c.h; y++) {
    for (let x = 0; x < c.w; x++) {
      if (c.get(x, y) !== null) continue;
      // Prefer the neighbour above (pixel is below the shape), then left/right, then below.
      const above = c.get(x, y - 1);
      const left = c.get(x - 1, y);
      const right = c.get(x + 1, y);
      const below = c.get(x, y + 1);
      if (above !== null && opts.ground) {
        add.push([x, y, opts.ground]);
        continue;
      }
      if (above !== null) add.push([x, y, darker(above, steps)]);
      else if (left !== null) add.push([x, y, darker(left, steps)]);
      else if (right !== null) add.push([x, y, darker(right, lit)]);
      else if (below !== null) add.push([x, y, darker(below, lit)]);
      else if (opts.diagonal) {
        const d = c.get(x - 1, y - 1) ?? c.get(x + 1, y - 1) ?? c.get(x - 1, y + 1) ?? c.get(x + 1, y + 1);
        if (d !== null) add.push([x, y, darker(d, steps)]);
      }
    }
  }
  for (const [x, y, col] of add) c.set(x, y, col);
  return c;
}

/**
 * Re-colours the existing 1-px rim of a shape (pixels that touch transparency) as a darker version of the
 * pixel just inside it — sel-out without growing the sprite. Useful when a sprite is already at its size limit.
 */
export function seloutInner(c: PixelCanvas, steps = 1): PixelCanvas {
  const edits: [number, number, PaletteKey][] = [];
  for (let y = 0; y < c.h; y++) {
    for (let x = 0; x < c.w; x++) {
      const p = c.get(x, y);
      if (p === null) continue;
      const edge = N4.some(([dx, dy]) => c.get(x + dx, y + dy) === null);
      if (edge) edits.push([x, y, darker(p, steps)]);
    }
  }
  for (const [x, y, col] of edits) c.set(x, y, col);
  return c;
}

/** Outline with one colour but only on the given sides of the shape ('b' = below, 'r' = right, …). */
export function outlineSides(c: PixelCanvas, col: PaletteKey, sides = 'br'): PixelCanvas {
  const add: number[] = [];
  for (let y = 0; y < c.h; y++) {
    for (let x = 0; x < c.w; x++) {
      if (c.get(x, y) !== null) continue;
      const hit =
        (sides.includes('b') && c.get(x, y - 1) !== null) ||
        (sides.includes('t') && c.get(x, y + 1) !== null) ||
        (sides.includes('r') && c.get(x - 1, y) !== null) ||
        (sides.includes('l') && c.get(x + 1, y) !== null);
      if (hit) add.push(y * c.w + x);
    }
  }
  for (const i of add) c.pixels[i] = col;
  return c;
}

/**
 * Filled ellipse shaded as a lit volume. `ramp` is dark → light; light comes from `light` (default top-left).
 * `bands` are the dot-product thresholds between ramp steps (auto-spread when omitted). Optional `rim`
 * brightens the far edge opposite the shadow (bounce light) by one step.
 */
export function sphere(
  c: PixelCanvas,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  ramp: readonly PaletteKey[],
  opts: { light?: [number, number]; bands?: number[]; rim?: boolean; only?: (x: number, y: number) => boolean } = {},
): PixelCanvas {
  const [lx0, ly0] = opts.light ?? [-0.55, -0.75];
  const ll = Math.hypot(lx0, ly0, 0.6);
  const [lx, ly, lz] = [lx0 / ll, ly0 / ll, 0.6 / ll];
  const n = ramp.length;
  const bands = opts.bands ?? Array.from({ length: n - 1 }, (_, i) => -0.15 + ((i + 1) / n) * 1.1);
  for (let y = Math.floor(cy - ry); y <= Math.ceil(cy + ry); y++) {
    for (let x = Math.floor(cx - rx); x <= Math.ceil(cx + rx); x++) {
      const nx = (x + 0.5 - cx) / rx;
      const ny = (y + 0.5 - cy) / ry;
      const d = nx * nx + ny * ny;
      if (d > 1) continue;
      if (opts.only && !opts.only(x, y)) continue;
      const nz = Math.sqrt(Math.max(0, 1 - d));
      const dot = nx * lx + ny * ly + nz * lz;
      let idx = 0;
      while (idx < bands.length && dot > (bands[idx] as number)) idx++;
      if (opts.rim && d > 0.72 && nx * lx + ny * ly > 0.35 && idx < 2) idx = Math.min(n - 1, idx + 1);
      c.set(x, y, ramp[Math.min(n - 1, idx)] as PaletteKey);
    }
  }
  return c;
}

/** Union of circles/ellipses filled with one colour — handy for organic blobs (canopies, bushes, rocks). */
export function blob(c: PixelCanvas, parts: readonly [number, number, number, number?][], col: Pixel): PixelCanvas {
  for (const [x, y, rx, ry] of parts) c.ellipse(x, y, rx, ry ?? rx, col);
  return c;
}

/** Deterministic 2-D value noise in [0,1) (smooth, tileable when `period` is set). */
export function valueNoise(x: number, y: number, seed = 0, scale = 4, period = 0): number {
  const h = (ix: number, iy: number) => {
    if (period) {
      ix = ((ix % period) + period) % period;
      iy = ((iy % period) + period) % period;
    }
    let n = (ix * 374761393 + iy * 668265263 + seed * 2246822519) >>> 0;
    n = ((n ^ (n >>> 13)) * 1274126177) >>> 0;
    return ((n ^ (n >>> 16)) >>> 0) / 4294967296;
  };
  const fx = x / scale;
  const fy = y / scale;
  const x0 = Math.floor(fx);
  const y0 = Math.floor(fy);
  const tx = fx - x0;
  const ty = fy - y0;
  const sx = tx * tx * (3 - 2 * tx);
  const sy = ty * ty * (3 - 2 * ty);
  const a = h(x0, y0) + (h(x0 + 1, y0) - h(x0, y0)) * sx;
  const b = h(x0, y0 + 1) + (h(x0 + 1, y0 + 1) - h(x0, y0 + 1)) * sx;
  return a + (b - a) * sy;
}

/** Ordered 4×4 Bayer threshold in [0,1) — for sparse, deliberate dithers on large flat areas only. */
export const bayer = (x: number, y: number): number =>
  ([0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5][(y & 3) * 4 + (x & 3)] as number) / 16;

/** Copy of a canvas moved by (dx, dy). */
export function shifted(c: PixelCanvas, dx: number, dy: number): PixelCanvas {
  const out = new PixelCanvas(c.w, c.h);
  out.blit(c, dx, dy);
  return out;
}

/** Recolours every pixel through `map` (unmapped colours untouched). */
export function remap(c: PixelCanvas, map: Partial<Record<PaletteKey, Pixel>>): PixelCanvas {
  for (let i = 0; i < c.pixels.length; i++) {
    const p = c.pixels[i] as Pixel;
    if (p !== null && map[p] !== undefined) c.pixels[i] = map[p] as Pixel;
  }
  return c;
}

/** Deterministic hash for scattering decoration by position (stable across builds). */
export const hash2 = (x: number, y: number, salt = 0): number =>
  (((x * 73856093) ^ (y * 19349663) ^ (salt * 83492791)) >>> 0) / 4294967296;
